import { useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { quotationService } from "../../services/quotationService";
import { getErrorMessage } from "../../utils/errors";
import type { QuotationPayload } from "../../types";
import "./QuotationForm.css";

interface QuotationFormProps {
  productId: string;
  productName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

type FormFields = {
  name: string;
  companyName: string;
  phone: string;
  city: string;
  email: string;
  description: string;
};

const emptyForm: FormFields = {
  name: "",
  companyName: "",
  phone: "",
  city: "",
  email: "",
  description: "",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

function QuotationForm({ productId, productName, onSuccess, onCancel }: QuotationFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormFields>(emptyForm);
  const [touched, setTouched] = useState<Partial<Record<keyof FormFields, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (fields: FormFields): Partial<Record<keyof FormFields, string>> => {
    const errors: Partial<Record<keyof FormFields, string>> = {};
    if (!fields.name.trim()) errors.name = t("quotation.validation.required");
    else if (fields.name.trim().length < 2) errors.name = t("quotation.validation.nameMin");
    if (!fields.phone.trim()) errors.phone = t("quotation.validation.required");
    else if (!PHONE_REGEX.test(fields.phone.trim())) errors.phone = t("quotation.validation.phoneInvalid");
    if (!fields.city.trim()) errors.city = t("quotation.validation.required");
    if (!fields.email.trim()) errors.email = t("quotation.validation.required");
    else if (!EMAIL_REGEX.test(fields.email.trim())) errors.email = t("quotation.validation.emailInvalid");
    return errors;
  };

  const errors = validate(form);

  const handleChange =
    (field: keyof FormFields) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleBlur = (field: keyof FormFields) => () => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const fieldError = (field: keyof FormFields) =>
    touched[field] ? errors[field] : undefined;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    if (Object.keys(errors).length > 0) {
      setTouched({ name: true, phone: true, city: true, email: true });
      return;
    }

    setSubmitting(true);
    try {
      const payload: QuotationPayload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        email: form.email.trim(),
        productId,
      };
      if (form.companyName.trim()) payload.companyName = form.companyName.trim();
      if (form.description.trim()) payload.description = form.description.trim();

      await quotationService.submitQuotation(payload);
      toast.success(t("quotation.success"));
      setForm(emptyForm);
      setTouched({});
      onSuccess();
    } catch (submitError) {
      toast.error(await getErrorMessage(submitError, t("quotation.error")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="quotation-form" onSubmit={handleSubmit} noValidate>
      {productName && (
        <p className="quotation-form__product">
          {t("quotation.forProduct")} <strong>{productName}</strong>
        </p>
      )}

      <div className="quotation-form__row">
        <div className="quotation-form__field">
          <label className="quotation-form__label">
            {t("quotation.name")} <span className="quotation-form__req">*</span>
          </label>
          <input
            type="text"
            className={`quotation-form__input ${fieldError("name") ? "quotation-form__input--error" : ""}`}
            value={form.name}
            onChange={handleChange("name")}
            onBlur={handleBlur("name")}
            maxLength={120}
            autoComplete="name"
          />
          {fieldError("name") && <span className="quotation-form__error">{fieldError("name")}</span>}
        </div>
        <div className="quotation-form__field">
          <label className="quotation-form__label">{t("quotation.companyName")}</label>
          <input
            type="text"
            className="quotation-form__input"
            value={form.companyName}
            onChange={handleChange("companyName")}
            maxLength={160}
            autoComplete="organization"
          />
        </div>
      </div>

      <div className="quotation-form__row">
        <div className="quotation-form__field">
          <label className="quotation-form__label">
            {t("quotation.phone")} <span className="quotation-form__req">*</span>
          </label>
          <input
            type="tel"
            className={`quotation-form__input ${fieldError("phone") ? "quotation-form__input--error" : ""}`}
            value={form.phone}
            onChange={handleChange("phone")}
            onBlur={handleBlur("phone")}
            maxLength={20}
            placeholder={t("quotation.phonePlaceholder")}
            autoComplete="tel"
          />
          {fieldError("phone") && <span className="quotation-form__error">{fieldError("phone")}</span>}
        </div>
        <div className="quotation-form__field">
          <label className="quotation-form__label">
            {t("quotation.city")} <span className="quotation-form__req">*</span>
          </label>
          <input
            type="text"
            className={`quotation-form__input ${fieldError("city") ? "quotation-form__input--error" : ""}`}
            value={form.city}
            onChange={handleChange("city")}
            onBlur={handleBlur("city")}
            maxLength={120}
          />
          {fieldError("city") && <span className="quotation-form__error">{fieldError("city")}</span>}
        </div>
      </div>

      <div className="quotation-form__field">
        <label className="quotation-form__label">
          {t("quotation.email")} <span className="quotation-form__req">*</span>
        </label>
        <input
          type="email"
          className={`quotation-form__input ${fieldError("email") ? "quotation-form__input--error" : ""}`}
          value={form.email}
          onChange={handleChange("email")}
          onBlur={handleBlur("email")}
          maxLength={160}
          autoComplete="email"
        />
        {fieldError("email") && <span className="quotation-form__error">{fieldError("email")}</span>}
      </div>

      <div className="quotation-form__field">
        <label className="quotation-form__label">{t("quotation.description")}</label>
        <textarea
          className="quotation-form__input quotation-form__textarea"
          value={form.description}
          onChange={handleChange("description")}
          rows={4}
          maxLength={2000}
          placeholder={t("quotation.descriptionPlaceholder")}
        />
      </div>

      <div className="quotation-form__actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={onCancel}
          disabled={submitting}
        >
          {t("admin.cancel")}
        </button>
        <button type="submit" className="btn btn-filled" disabled={submitting}>
          {submitting ? (
            <span className="quotation-form__loading">
              <span className="auth-spinner auth-spinner-white" />
            </span>
          ) : (
            t("quotation.submit")
          )}
        </button>
      </div>
    </form>
  );
}

export default QuotationForm;
